import { Component, EventEmitter, Inject, Injector, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { AbstractComponent, AgentTrendGPTService, TrendGPTFollowUpRequest, TrendGPTListedFollowUpAction } from 'lib-trend-core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DAYS_OF_WEEK, FollowUpAction, FollowUpConfig, TIME_OPTIONS } from './follow-up.model';
import { MatDialog } from '@angular/material/dialog';
import { WorkingHoursModalComponent } from './working-hours-modal/working-hours-modal.component';
import { AgentConfigRequest, MaxDelayMessageLimitTypeEnum, MessageGrouptTypeEnum, ModelAgentTypeEnum } from '@trendbuild/trend-gpt-maker-api';
import { firstValueFrom } from 'rxjs';

enum ConfigTabType {
  BEHAVIOR = 'BEHAVIOR',
  TIME = 'TIME',
  FOLLOW_UP = 'FOLLOW_UP'
}

interface StepIndexSignal {
  value?: number;
  update?: (fn: (current: number) => number) => void;
}

interface StepIndexSubject {
  next?: (value: number) => void;
}

interface StepIndexType extends StepIndexSignal, StepIndexSubject {
  value?: number;
  update?: (fn: (current: number) => number) => void;
  next?: (value: number) => void;
}

type ConfigsFormValue = AgentConfigRequest & { followUpActions: FollowUpAction[]; finishOnTime: number };

interface ApiWorkingDay {
  dayWeek?: number;
  day?: number;
  name?: string;
  active?: boolean;
  hours?: ApiHour[];
}

interface ApiHour {
  start?: string;
  startTime?: string;
  end?: string;
  endTime?: string;
}

interface ProcessedWorkingDay {
  dayWeek: number;
  active: boolean;
  hours: { start: string; end: string }[];
}

@Component({
    selector: 'app-agent-configs-form',
    templateUrl: './agent-configs-form.component.html',
    styleUrls: ['./agent-configs-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AgentConfigsFormComponent extends AbstractComponent {
  @Input() agentId!: string;
  @Input() stepIndex!: StepIndexType | number;
  @Output('previousStep') previousStepEmitter = new EventEmitter<number>();

  followUpConfig: FollowUpConfig | null = null;
  timeOptions = TIME_OPTIONS;
  activeConfigTab: ConfigTabType = ConfigTabType.BEHAVIOR;
  readonly ConfigTabType = ConfigTabType;

  constructor(
    injector: Injector,
    private dialog: MatDialog,
    private agentTrendGPTService: AgentTrendGPTService,
  ) {
    super(injector);
    this.setupForm();
  }

  private setupForm(): void {
    this.formGroup = this.formBuilder.group({
      enabledEmoji: [false],
      splitMessages: [false],
      prefferModel: [ModelAgentTypeEnum.GPT_4],
      enabledReminder: [false],
      limitSubjects: [false],
      timezone: [this.getTimezoneCompanyCurrentUser()],
      messageGroupingTime: [MessageGrouptTypeEnum.NO_GROUP],
      maxDailyMessages: [null],
      maxDailyMessagesLimitAction: [MaxDelayMessageLimitTypeEnum.BLOCK],
      knowledgeByFunction: [false],
      onLackKnowLedge: [null],
      followUpActions: this.formBuilder.array([]),
      finishOnTime: [3600]
    });
  }

  async ngOnInit(): Promise<void> {
    await this.getAgentConfigs();
    await this.getFollowUpConfig();
  }

  private async getAgentConfigs(): Promise<void> {
    try {
      const configs = await firstValueFrom(this.agentTrendGPTService.getConfigById(this.agentId));
      this.formGroup.patchValue(configs);
    } catch (error) {
      this.alertService.error(this.t('agents.configsForm.loadConfigError'));
    }
  }

  private async getFollowUpConfig(): Promise<void> {
    try {
      const response = await firstValueFrom(this.agentTrendGPTService.getFollowUpById(this.agentId));
      // GPT Maker answers { actions, finishAction }; a bare array is the shape this screen was first written for.
      // An empty list means nothing is configured yet: followUpConfig stays null and the next save creates it.
      const followUpActions: TrendGPTListedFollowUpAction[] = Array.isArray(response) ? response : response?.actions ?? [];

      if (followUpActions.length > 0) {
        const finishAction = followUpActions.find((action) => action.type === 'FINISH_INTERACTION');
        const messageActions = followUpActions.filter((action) => action.type === 'SEND_MESSAGE');

        this.followUpConfig = {
          finishAction: { seconds: Number(response?.finishAction?.seconds ?? finishAction?.seconds ?? 3600) },
          actions: messageActions.map((action) => {
            let mappedWorkingHours: ProcessedWorkingDay[] = [];
            
            if (action.workingHours && action.workingHours.days && Array.isArray(action.workingHours.days)) {
              mappedWorkingHours = action.workingHours.days.map((day: ApiWorkingDay) => ({
                dayWeek: day.dayWeek || day.day || parseInt(day.name?.replace('DAY_', '') || '1'),
                active: day.active !== undefined ? day.active : true,
                hours: Array.isArray(day.hours) ? day.hours.map((hour: ApiHour) => ({
                  start: hour.start || hour.startTime || '08:00',
                  end: hour.end || hour.endTime || '18:00'
                })) : [{ start: '08:00', end: '18:00' }]
              }));
              
              const existingDays = mappedWorkingHours.map(d => d.dayWeek);
              DAYS_OF_WEEK.forEach(day => {
                if (!existingDays.includes(day.value)) {
                  mappedWorkingHours.push({
                    dayWeek: day.value,
                    active: false,
                    hours: [{ start: '08:00', end: '18:00' }]
                  });
                }
              });
              
              mappedWorkingHours.sort((a, b) => a.dayWeek - b.dayWeek);
            }
            
            return {
              instructions: action.instructions || '',
              seconds: Number(action.seconds),
              allowAllHours: action.allowAllHours !== undefined ? action.allowAllHours : true,
              workingHours: mappedWorkingHours
            };
          })
        };
        
        this.setupFollowUpForm(this.followUpConfig);
      }
    } catch (error) {
      this.alertService.error(this.t('agents.configsForm.loadConfigError'));
    }
  }

  private setupFollowUpForm(followUp: FollowUpConfig): void {
    while (this.followUpActions.length) {
      this.followUpActions.removeAt(0);
    }

    if (followUp.actions && followUp.actions.length > 0) {
      followUp.actions.forEach(action => {
        this.addFollowUpAction(action);
      });
    }

    if (followUp.finishAction) {
      this.formGroup.get('finishOnTime')?.setValue(followUp.finishAction.seconds);
    }
  }

  get followUpActions(): FormArray {
    return this.formGroup.get('followUpActions') as FormArray;
  }

  addFollowUpAction(action?: FollowUpAction): void {
    const hasAllowAllHours = action && 'allowAllHours' in action;
    const allowAllHours = hasAllowAllHours ? action.allowAllHours : true;
    
    const actionForm = this.formBuilder.group({
      instructions: [action?.instructions || '', Validators.required],
      seconds: [action?.seconds || 300, Validators.required],
      allowAllHours: [allowAllHours],
      workingHours: [action?.workingHours || []]
    });
    
    actionForm.get('seconds')?.valueChanges.subscribe(seconds => {
      this.updateFinishOnTime();
    });

    this.followUpActions.push(actionForm);
    
    this.updateFinishOnTime();
  }

  removeFollowUpAction(index: number): void {
    this.followUpActions.removeAt(index);
    this.updateFinishOnTime();
  }

  openWorkingHoursModal(index: number): void {
    const actionForm = this.followUpActions.at(index) as FormGroup;
    const allowAllHours = actionForm.get('allowAllHours')?.value;
    const parsedAllowAllHours = allowAllHours === true;
    let workingHours = actionForm.get('workingHours')?.value || [];
    
    if (workingHours && typeof workingHours === 'object' && !Array.isArray(workingHours)) {
      if ((workingHours as { days?: ApiWorkingDay[] }).days && Array.isArray((workingHours as { days: ApiWorkingDay[] }).days)) {
        workingHours = (workingHours as { days: ApiWorkingDay[] }).days.map((day: ApiWorkingDay) => ({
          dayWeek: day.dayWeek || day.day || parseInt(day.name?.replace('DAY_', '') || '1'),
          active: day.active !== undefined ? day.active : true,
          hours: Array.isArray(day.hours) ? day.hours.map((hour: ApiHour) => ({
            start: hour.start || hour.startTime || '08:00',
            end: hour.end || hour.endTime || '18:00'
          })) : [{ start: '08:00', end: '18:00' }]
        }));
      }
    }
    
    const dialogRef = this.dialog.open(WorkingHoursModalComponent, {
      width: '600px',
      data: {
        allowAllHours: parsedAllowAllHours,
        workingHours: workingHours
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        actionForm.patchValue({
          allowAllHours: result.allowAllHours,
          workingHours: result.workingHours
        });
      }
    });
  }

  async save(): Promise<void> {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    // The follow-up fields share this form but belong to the idle-actions call, not to /settings
    const { followUpActions, finishOnTime, ...settings }: ConfigsFormValue = this.formGroup.getRawValue();

    try {
      await firstValueFrom(this.agentTrendGPTService.updateConfigById(this.agentId, settings));

      if (followUpActions.length > 0) {
        const followUpRequest: TrendGPTFollowUpRequest = {
          finishAction: { seconds: finishOnTime },
          actions: followUpActions
        };

        if (this.followUpConfig) {
          await firstValueFrom(this.agentTrendGPTService.updateFollowUpById(this.agentId, followUpRequest));
        } else {
          await firstValueFrom(this.agentTrendGPTService.createFollowUpById(this.agentId, followUpRequest));
        }
        // GPT Maker now holds this follow-up: the next save updates it instead of creating another
        this.followUpConfig = followUpRequest;
      } else if (this.followUpConfig) {
        await firstValueFrom(this.agentTrendGPTService.deleteFollowUpById(this.agentId));
        this.followUpConfig = null;
      }

      this.alertService.success(this.t('agents.configsForm.saveSuccess'));
    } catch (error) {
      this.alertService.error(this.t('agents.configsForm.saveError'));
    }
  }

  getTimeLabel(seconds: number): string {
    const option = this.timeOptions.find(t => t.value === seconds);
    return option ? option.label : `${seconds} segundos`;
  }
  
  getNextAvailableTime(currentSeconds: number): number {
    const currentIndex = this.timeOptions.findIndex(t => t.value === currentSeconds);
    
    if (currentIndex !== -1 && currentIndex < this.timeOptions.length - 1) {
      return this.timeOptions[currentIndex + 1].value;
    }
    
    if (currentIndex === this.timeOptions.length - 1) {
      // Already the longest window: GPT Maker refuses any value outside TIME_OPTIONS
      return currentSeconds;
    }
    
    const nextTime = this.timeOptions.find(t => t.value > currentSeconds);
    if (nextTime) {
      return nextTime.value;
    }
    
    return this.timeOptions[this.timeOptions.length - 1].value;
  }
  
  updateFinishOnTime(): void {
    if (this.followUpActions.length === 0) {
      return;
    }
    
    let maxActionTime = 0;
    this.followUpActions.controls.forEach(control => {
      const seconds = control.get('seconds')?.value || 0;
      if (seconds > maxActionTime) {
        maxActionTime = seconds;
      }
    });
    
    const nextAvailableTime = this.getNextAvailableTime(maxActionTime);
    const currentFinishTime = this.formGroup.get('finishOnTime')?.value || 0;
    
    if (currentFinishTime <= maxActionTime) {
      this.formGroup.get('finishOnTime')?.setValue(nextAvailableTime);
    }
  }


  public get modelTypes(): { label: string; value: ModelAgentTypeEnum; credits: number }[] {
    return this.enumKeys(ModelAgentTypeEnum).map(label => {
      const value = ModelAgentTypeEnum[label as keyof typeof ModelAgentTypeEnum];
      return { label: this.translateModelLabel(label), value, credits: this.getCreditsByKey(label) };
    });
  }

  private enumKeys<T extends Record<string, unknown>>(enumObj: T): string[] {
    return Object.keys(enumObj).filter(k => isNaN(Number(k)));
  }

  private getCreditsByKey(key: string): number {
    const normalized = key.toLowerCase().replace(/[^a-z0-9]/g, '');
    const creditsMap: Record<string, number> = {
      gpt5: 4,
      gpt5mini: 1,
      gpt41: 4,
      gpt41mini: 1,
      o4mini: 3,
      o3: 5,
      gpt4o: 5,
      gpt4omini: 1,
      o3mini: 3,
      openaio3mini: 3,
      openaio4mini: 3,
      deepinfrallama33: 1,
      openaio1: 25,
      openaio3: 5,
      gpt4: 10,
      qwen25max: 8,
      gpt4turbo: 20,
      claude35sonnet: 10,
      claude37sonnet: 10,
      claude35haiku: 2,
      sabia31: 3,
      sabia3: 3,
      deepseekchat: 1,
    };
    return creditsMap[normalized] ?? 0;
  }

  private translateModelLabel(label: string): string {
    const translations: Record<string, string> = {
      GPT_5: 'GPT-5',
      GPT_5_MINI: 'GPT-5 Mini',
      GPT_4_1: 'GPT-4.1',
      GPT_4_1_MINI: 'GPT-4.1 Mini',
      GPT_4_O: 'GPT-4o',
      GPT_4_O_MINI: 'GPT-4o Mini',
      OPEN_AI_O3: 'o3',
      OPEN_AI_O1: 'o1',
      OPEN_AI_O3_MINI: 'o3 Mini',
      OPEN_AI_O4_MINI: 'o4 Mini',
      GPT_4: 'GPT-4',
      CLAUDE_3_5_SONNET: 'Claude 3.5 Sonnet',
      CLAUDE_3_7_SONNET: 'Claude 3.7 Sonnet',
      CLAUDE_3_5_HAIKU: 'Claude 3.5 Haiku',
      DEEPINFRA_LLAMA3_3: 'Llama 3.3',
      QWEN_2_5_MAX: 'Qwen 2.5 Max',
      DEEPSEEK_CHAT: 'DeepSeek Chat',
      SABIA_3: 'Sabia 3',
      SABIA_3_1: 'Sabia 3.1',
    };
    return translations[label] ?? label;
  }

  changeConfigTab(tab: ConfigTabType): void {
    this.activeConfigTab = tab;
  }

  previousStep(): void {
    const current = (() => {
      if (typeof this.stepIndex === 'number') return this.stepIndex;
      if (this.stepIndex && (this.stepIndex as StepIndexType).value !== undefined) return (this.stepIndex as StepIndexType).value;
      return 0;
    })();

    const newIndex = Math.max(current - 1, 0);

    if (this.stepIndex && typeof (this.stepIndex as StepIndexType).update === 'function') {
      (this.stepIndex as StepIndexType).update(() => newIndex);
      return;
    }

    if (this.stepIndex && typeof (this.stepIndex as StepIndexType).next === 'function') {
      (this.stepIndex as StepIndexType).next(newIndex);
      return;
    }

    this.previousStepEmitter.emit(newIndex);
  }

}
